import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Logo } from "@/components/ui/logo"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { AccountStatus } from "@/components/account-status"
// import { RotatingText } from "@/components/rotating-text"
import { Phone, Loader2, Calendar, Activity, BarChart3, MoreHorizontal, LogOut, Menu, X, Feather, Mic, Chrome, ArrowRight } from "lucide-react"
import { startGuestSession } from "@/lib/guest"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { VoiceWidget } from "@/components/voice-widget"
import { useXAuth } from '@/hooks/use-x-auth'
import { LandingDemoChat } from '@/components/landing-demo-chat'
import { ProductFlowLoop } from '@/components/product-flow-loop'
import { useAuth } from '@/components/providers/auth-provider'
import { useMutation, useQuery, useLazyQuery, useSubscription } from '@apollo/client'
import { useToast } from '@/hooks/use-toast'
import { INITIATE_VOICE_CALL, GET_INCOMING_CALL_NUMBER, GET_DEMO_CALL_SIGNED_URL, DEMO_EVENTS_SUBSCRIPTION, END_DEMO_CALL, GET_DEMO_MINUTES_STATUS, GET_SIGNED_URL } from '@/lib/graphql/voice'
import { pulse } from '@/lib/pulse'
import { useConversation } from '@elevenlabs/react'
import { Testimonials } from '@/components/testimonials'
import { Footer } from '@/components/footer'

const faqItems = [
  {
    question: "How does X11.Social work?",
    answer: "Give it an idea: type it in the chat, say it on a call, or open the Chrome extension on the page you are reading. It writes a few versions in your voice, scores each one, and nothing goes out until you approve it."
  },
  {
    question: "Can I try it without an account?",
    answer: "Yes. The box at the top of this page turns one idea into three scored takes. The full demo opens the dashboard as Ada, a made-up founder, for one idea. Voice and posting need your own X account."
  },
  {
    question: "How does it learn my voice?",
    answer: "From your recent posts, any accounts you pick as style references, and your angle: a short interview about what you do and the stories you tell. Drafts only make first-person claims that something you said backs up."
  },
  {
    question: "What does the score mean?",
    answer: "Every draft gets a 1 to 5 virality score from our own model, trained on real X posts, with the hook, trend fit and call to action shown separately. Treat it as a guide, not a promise."
  },
  {
    question: "What is Inspiration?",
    answer: "Posts that did far better than their author usually does, from your niche and from what is trending generally, plus trending topics. Twist one into your own take in a click."
  },
  {
    question: "Can I edit posts before they go live?",
    answer: "Always. Wherever a post appears, in drafts, the queue or the chat, edit it by hand or ask the mini chat for a rewrite and pick the version you like."
  },
  {
    question: "Is there a Chrome extension?",
    answer: "Yes. Press the backtick key or Alt+T on any page and the chat drops down with that page as context: a tweet, a video, a repo, an article."
  },
  {
    question: "What's included in the voice minutes?",
    answer: "Every minute on a call or in voice mode. Text chat, drafting and scoring don't use minutes."
  },
  {
    question: "What happens if I run out of minutes?",
    answer: "Text chat keeps working. Voice minutes top up with the next weekly billing cycle."
  },
  {
    question: "How do scheduled calls work?",
    answer: "Pick a time and how often, like every morning at 7 or every Monday. We call you then. Miss one and you can call back whenever."
  },
  {
    question: "Is there a free trial?",
    answer: "Yes, 7 days on the plan. Cancel before it ends and you pay nothing."
  },
  {
    question: "Is my content kept private?",
    answer: "Yes. Your conversations and drafts are private and never shared with third parties."
  },
  {
    question: "Can I cancel anytime?",
    answer: "Yes. Cancel with one click. No questions asked."
  },
  {
    question: "What does \"x11.social\" mean?",
    answer: "X for the platform. 11 for voice AI, originally inspired by ElevenLabs. We build more of the voice stack ourselves now, but kept the name."
  }
]

// A/B test winner: "mixed" variant with 17.23% CTR (5x better than others)
// Keeping only the winning variant active
const heroVariations = [
  {
    id: 'mixed',
    headline: (
      <div className="leading-tight">
        <span className="font-bold block">How many posts did you forget this week?</span>
      </div>
    ),
    subheadline: 'Grow your 𝕏 account with quality content. Just talk.',
    buttonLayout: 'trial-first',
    trialButtonText: 'Start 7-Day Free Trial',
    demoButtonText: 'Try Free Demo Call'
  },
];

export default function LandingPage() {
  const navigate = useNavigate()
  const { initiateXAuth } = useXAuth()
  const { user, isAuthenticated, logout } = useAuth()
  const { toast } = useToast()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [analyzerHandle, setAnalyzerHandle] = useState('')
  const [openingDemo, setOpeningDemo] = useState(false)
  const [demoError, setDemoError] = useState<string | null>(null)
  const [demoCallActive, setDemoCallActive] = useState(false)
  const [demoSessionId, setDemoSessionId] = useState<string | null>(null)
  const [userCallActive, setUserCallActive] = useState(false)
  
  // Detect operating system for keyboard shortcuts
  const [isMac, setIsMac] = useState(false)
  useEffect(() => {
    setIsMac(navigator.platform.toUpperCase().indexOf('MAC') >= 0 || navigator.userAgent.toUpperCase().indexOf('MAC') >= 0)
  }, [])

  const [fetchDemoSignedUrl] = useLazyQuery(GET_DEMO_CALL_SIGNED_URL, {
    fetchPolicy: 'network-only'
  })
  const [fetchUserSignedUrl] = useLazyQuery(GET_SIGNED_URL)
  
  // Fetch demo minutes status
  const { data: demoMinutesData, refetch: refetchDemoMinutes } = useQuery(GET_DEMO_MINUTES_STATUS, {
    skip: isAuthenticated, // Only fetch for non-authenticated users
    fetchPolicy: 'network-only'
  })
  
  // Initialize ElevenLabs conversation
  const conversation = useConversation({
    onConnect: () => {
      console.log('Connected to ElevenLabs')
      toast({
        title: "Connected",
        description: "You're now connected to our AI assistant. Start speaking!",
      })
    },
    onDisconnect: async () => {
      console.log('Disconnected from ElevenLabs')
      
      // Always reset states on disconnect
      const wasDemoCall = demoCallActive
      const wasUserCall = userCallActive
      const sessionId = demoSessionId
      
      // Reset states immediately
      setDemoCallActive(false)
      setUserCallActive(false)
      setDemoSessionId(null)
      
      // Check if it was a demo call
      if (wasDemoCall && sessionId) {
        try {
          await endDemoCallMutation({
            variables: { sessionId: sessionId }
          })
          console.log(`📊 Demo call duration recorded for session: ${sessionId}`)
          // Refetch demo minutes to update the remaining balance
          if (refetchDemoMinutes) {
            refetchDemoMinutes()
          }
        } catch (error) {
          console.error('Failed to record demo call duration:', error)
        }
        
        toast({
          title: "Demo Call Ended",
          description: "Thanks for trying our demo! Sign up to get your own AI assistant.",
        })
      }
      
      // Check if it was a user call
      if (wasUserCall) {
        toast({
          title: "Call Ended",
          description: "Your voice call has ended.",
        })
      }
    },
    onError: (error) => {
      console.error('ElevenLabs error:', error)
      // Always reset states on error
      setDemoCallActive(false)
      setUserCallActive(false)
      setDemoSessionId(null)
      toast({
        title: "Connection Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      })
    },
    onMessage: (message) => {
      console.log('ElevenLabs message:', message)
    },
    onDebug: (debug) => {
      console.log('ElevenLabs debug:', debug)
    }
  })

  // Redirect authenticated users to chat
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/chat')
    }
  }, [isAuthenticated, navigate])
  const [heroVariant, setHeroVariant] = useState(heroVariations[0])

  useEffect(() => {
    // Check if user has a stored variant preference
    const storedVariantId = localStorage.getItem('hero-variant')
    if (storedVariantId) {
      const variant = heroVariations.find(v => v.id === storedVariantId)
      if (variant) {
        setHeroVariant(variant)
        return
      }
    }

    // Otherwise, randomly select a variant for A/B testing
    const randomIndex = Math.floor(Math.random() * heroVariations.length)
    const selectedVariant = heroVariations[randomIndex]
    setHeroVariant(selectedVariant)
    localStorage.setItem('hero-variant', selectedVariant.id)

    // Track which variant was shown
    pulse.trackEvent('hero_variant_shown', {
      variant_id: selectedVariant.id,
      authenticated: isAuthenticated
    })
  }, [])
  
  const [endDemoCallMutation] = useMutation(END_DEMO_CALL)
  
  const [initiateCall, { loading: callLoading }] = useMutation(INITIATE_VOICE_CALL, {
    onCompleted: () => {
      toast({
        title: "Call Initiated Successfully!",
        description: "Your call is being connected. Check your phone in a moment.",
      })
    },
    onError: (error) => {
      toast({
        title: "Call Failed",
        description: error.message,
        variant: "destructive"
      })
    }
  })

  const { data: incomingCallData } = useQuery(GET_INCOMING_CALL_NUMBER, {
    skip: !isAuthenticated || Boolean((user as any)?.isGuest)
  })
  
  const incomingCallNumber = incomingCallData?.getIncomingCallNumber || 'x11.social'
  
  // Subscribe to demo events when we have a session
  useSubscription(DEMO_EVENTS_SUBSCRIPTION, {
    variables: { sessionId: demoSessionId },
    skip: !demoSessionId || !demoCallActive,
    onData: ({ data }) => {
      if (data?.data?.demoEvents) {
        const event = data.data.demoEvents
        console.log('📢 Demo event received:', event)
        
        // Show toast notifications for different event types
        switch (event.type) {
          case 'POST_CREATED':
          case 'POST_PUBLISHED':
            toast({
              title: event.title || "Post Created!",
              description: (
                <div className="space-y-2">
                  <p>{event.description || event.content}</p>
                  {event.url && (
                    <Button
                      size="sm"
                      asChild
                      className="mt-2 bg-foreground text-background hover:bg-foreground/90"
                    >
                      <a 
                        href={event.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                        </svg>
                        View on X
                      </a>
                    </Button>
                  )}
                </div>
              ),
              duration: 10000, // Show for 10 seconds
            })
            break
            
          case 'DRAFT_SAVED':
            toast({
              title: "Draft Saved",
              description: event.description || "Your post has been saved as a draft",
            })
            break
            
          case 'STYLE_ANALYZED':
            toast({
              title: "Style Analysis",
              description: event.description || "AI is analyzing your writing style",
            })
            break
        }
      }
    }
  })
  
  // User events subscription is now handled site-wide in AuthProvider

  const handleSelectPlan = (planId: string) => {
    // Track plan selection
    pulse.trackPlanSelected(planId)
    
    // Store selected plan and navigate to onboarding
    localStorage.setItem('selected-plan', planId)
    navigate('/onboarding')
  }

  const handleSignIn = () => {
    initiateXAuth()
  }

  const handleDemoCall = async () => {
    // End any existing session first
    if (conversation.status === 'connected') {
      await conversation.endSession()
    }
    
    setDemoCallActive(true)
    
    try {
      // Get signed URL from the server with session ID
      const sessionId = pulse.getSessionId()
      setDemoSessionId(sessionId) // Store for subscription
      
      const { data } = await fetchDemoSignedUrl({
        variables: { 
          agentId: 'agent_6001k2ey0q9ze8cs26n0ccgm677w',
          sessionId: sessionId
        }
      })
      
      if (data?.getDemoCallSignedUrl) {
        // Start the conversation session with the signed URL and dynamic variables
        await conversation.startSession({
          signedUrl: data.getDemoCallSignedUrl,
          dynamicVariables: {
            sessionId: sessionId
          }
        })
        
        // Only keep the button in loading state if we're actually connecting
        // The onConnect/onDisconnect handlers will manage the state from here
      } else {
        throw new Error('No signed URL received')
      }
    } catch (error: any) {
      console.error('Failed to start demo call:', error)
      setDemoCallActive(false)
      setDemoSessionId(null) // Also clear session ID on error
      
      // Check if it's a rate limit error
      const errorMessage = error?.graphQLErrors?.[0]?.message || error?.message || 'Please try again later.'
      const isRateLimit = errorMessage.toLowerCase().includes('rate limit')
      
      toast({
        title: isRateLimit ? "Demo Call Limit Reached" : "Failed to Start Demo",
        description: errorMessage,
        variant: "destructive"
      })
    }
  }
  
  // Handle ending the demo call
  const handleEndDemoCall = async () => {
    if (conversation.status === 'connected') {
      await conversation.endSession()
    }
    
    // Record the demo call duration
    if (demoSessionId) {
      try {
        await endDemoCallMutation({
          variables: { sessionId: demoSessionId }
        })
        console.log(`📊 Demo call duration recorded for session: ${demoSessionId}`)
        // Refetch demo minutes to update the remaining balance
        if (refetchDemoMinutes) {
          refetchDemoMinutes()
        }
      } catch (error) {
        console.error('Failed to record demo call duration:', error)
      }
    }
    
    setDemoCallActive(false)
    setDemoSessionId(null)
  }

  // Handle phone call (original functionality)
  const handleCallNow = async () => {
    if (!user?.phoneVerified) {
      toast({
        title: "Phone Verification Required",
        description: "Please verify your phone number before making calls",
        variant: "destructive"
      })
      return
    }

    if (!user?.credits || user.credits < 1) {
      toast({
        title: "Insufficient Credits",
        description: "You need at least 1 minute of credits to make a call",
        variant: "destructive"
      })
      return
    }

    // Track call initiation
    pulse.trackCallInitiated()
    
    await initiateCall()
  }
  
  // Handle voice widget call (new functionality)
  const handleVoiceWidget = async () => {
    // End any existing session first
    if (conversation.status === 'connected') {
      await conversation.endSession()
    }
    
    setUserCallActive(true)
    
    try {
      // Get signed URL for authenticated user
      const { data } = await fetchUserSignedUrl()
      
      if (data?.getSignedUrl) {
        // Connect to ElevenLabs with the signed URL
        await conversation.startSession({
          signedUrl: data.getSignedUrl
        })
      } else {
        throw new Error('No signed URL received')
      }
    } catch (error: any) {
      console.error('Failed to start voice call:', error)
      setUserCallActive(false)
      
      const errorMessage = error?.graphQLErrors?.[0]?.message || error?.message || 'Please try again later.'
      const isInsufficientCredits = errorMessage.toLowerCase().includes('insufficient credits')
      
      toast({
        title: isInsufficientCredits ? "Insufficient Credits" : "Failed to Start Call",
        description: errorMessage,
        variant: "destructive"
      })
    }
  }
  
  // Handle ending the user call
  const handleEndUserCall = async () => {
    if (conversation.status === 'connected') {
      await conversation.endSession()
    }
    setUserCallActive(false)
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="px-6 py-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between">
          <Logo variant="header" />
          
          {isAuthenticated ? (
            <>
              {/* Desktop Navigation */}
              <div className="hidden md:flex items-start gap-4">
                <div className="flex flex-col items-center gap-1">
                  <Button 
                    onClick={handleCallNow}
                    disabled={callLoading}
                    className="bg-foreground hover:bg-foreground/90 text-background"
                  >
                    {callLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        <Phone className="w-4 h-4 mr-2" />
                        Call Me Now
                      </>
                    )}
                  </Button>
                  <div className="flex items-center gap-1 text-xs">
                    <span className="text-muted-foreground">call</span>
                    <span className="font-semibold text-foreground">{incomingCallNumber}</span>
                    <span className="text-muted-foreground">to post</span>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  onClick={handleVoiceWidget}
                  className="hover:bg-accent"
                  title="Start voice call in browser"
                >
                  <Mic className="w-4 h-4 mr-2" />
                  Voice
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/chat')}
                  className="hover:bg-accent"
                >
                  <Feather className="w-4 h-4 mr-2" />
                  Creator
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/virality')}
                  className="hover:bg-accent"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Scoreboar Analyzer
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-3 p-2 rounded-full hover:bg-accent transition-colors">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={user?.xProfileImage} alt={user?.xUsername} loading="lazy" />
                        <AvatarFallback className="bg-foreground text-background text-sm font-bold">
                          {(user?.xDisplayName || user?.xUsername || 'U').charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col items-start">
                        <span className="text-sm font-bold text-foreground leading-none">
                          {user?.xDisplayName || user?.xUsername}
                        </span>
                        <span className="text-xs text-muted-foreground leading-none">
                          @{user?.xUsername}
                        </span>
                      </div>
                      <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem
                      onClick={() => navigate('/chat')}
                      className="hover:bg-accent"
                    >
                      <Feather className="w-4 h-4 mr-2" />
                      Creator Chat
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate('/scheduler')}
                      className="hover:bg-accent"
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Scheduler
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate('/profile')}
                      className="hover:bg-accent"
                    >
                      <Activity className="w-4 h-4 mr-2" />
                      Profile & Credits
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => {
                        pulse.trackEvent('add_to_chrome_clicked', {
                          location: 'profile_dropdown',
                          authenticated: true
                        })
                        window.open('https://chromewebstore.google.com/detail/fnpbcidkcfbbijjnibgfhdablepmmefk', '_blank')
                      }}
                      className="hover:bg-accent"
                    >
                      <Chrome className="w-4 h-4 mr-2" />
                      Chrome Extension
                    </DropdownMenuItem>
                    <div className="border-t border-border my-1" />
                    <DropdownMenuItem onClick={logout} className="text-destructive focus:text-destructive">
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign out @{user?.xUsername}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Mobile Navigation */}
              <div className="md:hidden flex items-center gap-2">
                <Button 
                  onClick={handleCallNow}
                  disabled={callLoading}
                  size="sm"
                  className="bg-foreground hover:bg-foreground/90 text-background flex items-center gap-2"
                >
                  {callLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Calling...</span>
                    </>
                  ) : (
                    <>
                      <Phone className="w-4 h-4" />
                      <span>Call Me Now</span>
                    </>
                  )}
                </Button>
                <Button 
                  onClick={handleVoiceWidget}
                  size="icon"
                  variant="outline"
                  className="hover:bg-accent"
                  title="Voice call"
                >
                  <Mic className="w-4 h-4" />
                </Button>
                <Button
                  onClick={() => navigate('/chat')}
                  size="icon"
                  variant="outline"
                  className="hover:bg-accent"
                >
                  <Feather className="w-4 h-4" />
                </Button>
                <Button
                  onClick={() => navigate('/virality')}
                  size="icon"
                  variant="outline"
                  className="hover:bg-accent"
                >
                  <BarChart3 className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="p-2"
                >
                  {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Desktop layout */}
              <div className="hidden md:flex items-center gap-2">
                <Button
                  variant="outline"
                  className="hover:bg-accent"
                  onClick={() => navigate('/virality')}
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Scoreboar Analyzer
                </Button>
                <Button 
                  variant="ghost" 
                  className="text-muted-foreground hover:text-foreground flex items-center gap-2"
                  onClick={handleSignIn}
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                  Sign in with X
                </Button>
              </div>

              {/* Mobile Sign In and Theme */}
              <div className="md:hidden flex items-center gap-2">
                <Button
                  onClick={() => navigate('/virality')}
                  size="icon"
                  variant="outline"
                  className="hover:bg-accent"
                >
                  <BarChart3 className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-foreground flex items-center gap-2"
                  onClick={handleSignIn}
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                  Sign in with X
                </Button>
              </div>
            </>
          )}
            </div>
          </div>

          {/* Mobile Menu Dropdown */}
          {isAuthenticated && mobileMenuOpen && (
            <div className="md:hidden mt-4 py-4 border-t border-border/50">
              <div className="space-y-4">
              <div className="flex items-center gap-2 px-2">
                <Phone className="w-3 h-3 text-muted-foreground" />
                <span className="text-sm font-bold text-foreground">{incomingCallNumber}</span>
                <span className="text-xs text-muted-foreground font-medium">to post</span>
              </div>
              

              <Button
                variant="ghost"
                className="w-full justify-start text-muted-foreground hover:text-foreground"
                onClick={() => {
                  navigate('/chat')
                  setMobileMenuOpen(false)
                }}
              >
                <Feather className="w-4 h-4 mr-2" />
                Creator Chat
              </Button>

              <Button
                variant="ghost"
                className="w-full justify-start text-muted-foreground hover:text-foreground"
                onClick={() => {
                  navigate('/scheduler')
                  setMobileMenuOpen(false)
                }}
              >
                <Calendar className="w-4 h-4 mr-2" />
                Scheduler
              </Button>

              <Button
                variant="ghost"
                className="w-full justify-start text-muted-foreground hover:text-foreground"
                onClick={() => {
                  navigate('/profile')
                  setMobileMenuOpen(false)
                }}
              >
                <Activity className="w-4 h-4 mr-2" />
                Profile & Credits
              </Button>

              <div className="border-t border-border pt-4">
                <div className="flex items-center gap-3 px-2 mb-3">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.xProfileImage} alt={user?.xUsername} />
                    <AvatarFallback className="bg-foreground text-background text-sm font-bold">
                      {(user?.xDisplayName || user?.xUsername || 'U').charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col">
                    <span className="text-sm font-bold text-foreground">
                      {user?.xDisplayName || user?.xUsername}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      @{user?.xUsername}
                    </span>
                  </div>
                </div>

                <Button
                  variant="ghost"
                  className="w-full justify-start hover:bg-accent"
                  onClick={() => {
                    pulse.trackEvent('add_to_chrome_clicked', {
                      location: 'mobile_menu',
                      authenticated: true
                    })
                    window.open('https://chromewebstore.google.com/detail/fnpbcidkcfbbijjnibgfhdablepmmefk', '_blank')
                    setMobileMenuOpen(false)
                  }}
                >
                  <Chrome className="w-4 h-4 mr-2" />
                  Chrome Extension
                </Button>

                <Button
                  variant="ghost"
                  className="w-full justify-start text-destructive hover:text-destructive/80"
                  onClick={() => {
                    logout()
                    setMobileMenuOpen(false)
                  }}
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign out
                </Button>
              </div>
            </div>
          </div>
        )}        </div>
      </header>

      {/* Hero: the pitch on the left, the product working on the right */}
      <section className="relative overflow-hidden px-6 pb-20 pt-28 md:pt-32">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-[560px] bg-[radial-gradient(55%_60%_at_72%_32%,hsl(var(--foreground)/0.07),transparent_70%)]"
        />
        <div className="relative mx-auto grid max-w-6xl grid-cols-1 items-start gap-12 lg:grid-cols-[1.05fr_1fr] lg:gap-16">
          <div className="text-left lg:pt-8">
            <h1 className="text-4xl font-semibold leading-[1.08] tracking-tight text-foreground md:text-5xl lg:text-[3.4rem]">
              {heroVariant.headline}
            </h1>
            <p className="mt-5 max-w-[34rem] text-lg leading-relaxed text-muted-foreground">
              {heroVariant.subheadline}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              {isAuthenticated ? (
                <Button size="lg" className="key-caps h-12 px-7 text-[15px]" onClick={() => navigate('/chat')}>
                  Open Create
                  <ArrowRight className="h-4 w-4" />
                </Button>
              ) : (
                <Button
                  size="lg"
                  className="key-caps h-12 px-7 text-[15px]"
                  disabled={openingDemo}
                  onClick={async () => {
                    pulse.trackEvent('hero_cta_clicked', { variant_id: heroVariant.id, button_type: 'guest_demo', button_position: 'first', authenticated: isAuthenticated })
                    setOpeningDemo(true)
                    setDemoError(null)
                    try {
                      await startGuestSession('/chat')
                    } catch (error: any) {
                      setOpeningDemo(false)
                      setDemoError(error?.message ?? 'The demo is busy. Try again in a minute.')
                    }
                  }}
                >
                  {openingDemo ? 'Opening the demo' : 'Try the demo'}
                  <ArrowRight className="h-4 w-4" />
                </Button>
              )}
              <Button
                size="lg"
                variant="outline"
                className="h-12 px-6"
                onClick={() => {
                  pulse.trackEvent('hero_cta_clicked', { variant_id: heroVariant.id, button_type: 'trial', button_position: 'second', authenticated: isAuthenticated })
                  localStorage.setItem('selected-plan', 'creator')
                  navigate('/onboarding')
                }}
              >
                {heroVariant.trialButtonText || 'Start 7-day trial'}
              </Button>
            </div>
            {!isAuthenticated && (
              <p className="mt-4 text-sm text-muted-foreground">
                The demo needs no account. Rather talk it out?{' '}
                <button
                  type="button"
                  disabled={demoCallActive || conversation.status === 'connecting'}
                  onClick={() => {
                    pulse.trackEvent('hero_cta_clicked', { variant_id: heroVariant.id, button_type: 'demo', button_position: 'link', authenticated: isAuthenticated })
                    handleDemoCall()
                  }}
                  className="font-medium text-foreground underline decoration-border underline-offset-4 hover:decoration-foreground"
                >
                  {demoCallActive
                    ? conversation.status === 'connected' ? 'Voice demo is live' : 'Connecting'
                    : 'Call the voice demo'}
                </button>
              </p>
            )}
            {demoError && <p className="mt-2 text-sm text-destructive">{demoError}</p>}
          </div>

          <div className="relative mx-auto w-full max-w-md lg:mt-8 lg:max-w-none">
            <LandingDemoChat onSignIn={handleSignIn} className="relative" />
          </div>
        </div>

        <form
          className="relative mx-auto mt-16 flex w-full max-w-md items-center gap-2"
          onSubmit={(event: React.FormEvent) => {
            event.preventDefault()
            const handle = analyzerHandle.trim().replace('@', '')
            if (handle) navigate(`/virality?handle=${encodeURIComponent(handle)}`)
          }}
        >
          <Input
            value={analyzerHandle}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setAnalyzerHandle(event.target.value)}
            placeholder="@yourhandle"
            className="h-10 bg-background"
            aria-label="Your X handle"
          />
          <Button type="submit" variant="outline" className="h-10 shrink-0">
            Score my account
          </Button>
        </form>
        <p className="relative mt-1.5 text-center text-xs text-muted-foreground">
          Free: what works in your last posts, what flops, when to post.
        </p>

        {/* The pain, and the fix: three equal cards, each shows its point */}
        <div className="relative mx-auto mt-20 grid max-w-5xl grid-cols-1 gap-4 text-left md:grid-cols-3">
          <div className="slab flex flex-col rounded-2xl border border-border bg-card p-5">
            <div className="surface-slab flex h-40 flex-col justify-center rounded-xl border border-border/70 bg-muted/40 p-4" aria-hidden="true">
              <div className="grid grid-cols-7 gap-1.5 text-center text-[10px] text-muted-foreground">
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, index) => <span key={index}>{day}</span>)}
                {[1, 1, 1, 0, 1, 0, 1, 1, 0, 0, 0, 0, 0, 0].map((posted, index) => (
                  <span
                    key={index}
                    className={`aspect-square rounded-[5px] ${posted ? 'bg-foreground' : 'border border-dashed border-border bg-background'}`}
                  />
                ))}
              </div>
              <p className="mt-2.5 text-[11px] text-muted-foreground">Launch week. Then a busy one.</p>
            </div>
            <h3 className="mt-5 font-semibold">You stop posting when work gets busy</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
              Consistency goes first. Call while you drive; the posts are ready by the time you park.
            </p>
          </div>

          <div className="slab flex flex-col rounded-2xl border border-border bg-card p-5">
            <div className="surface-slab flex h-40 flex-col justify-center gap-2 rounded-xl border border-border/70 bg-muted/40 p-4" aria-hidden="true">
              <p className="w-fit max-w-[92%] rounded-2xl rounded-bl-md bg-background px-3 py-2 text-[12px] italic leading-snug text-muted-foreground shadow-sm">
                so basically we stopped doing standups and like, nobody even noticed
              </p>
              <p className="ml-auto w-fit max-w-[92%] rounded-2xl rounded-br-md bg-foreground px-3 py-2 text-[12px] leading-snug text-background">
                We stopped doing standups. Nobody noticed.
              </p>
            </div>
            <h3 className="mt-5 font-semibold">Writing feels like work</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
              The ideas are there; typing kills them. Say it the way you would text it. It comes back in your voice.
            </p>
          </div>

          <div className="slab flex flex-col rounded-2xl border border-border bg-card p-5">
            <div className="surface-slab flex h-40 flex-col justify-center gap-2 rounded-xl border border-border/70 bg-muted/40 p-4" aria-hidden="true">
              <div className="flex items-center justify-between gap-3 rounded-lg bg-background px-3 py-2 shadow-sm">
                <span className="text-[12px] leading-snug">We stopped doing standups. Nobody noticed.</span>
                <span className="key key-ink shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-medium tabular-nums">3.8/5</span>
              </div>
              <div className="flex items-center justify-between gap-3 rounded-lg bg-background/60 px-3 py-2">
                <span className="text-[12px] leading-snug text-muted-foreground line-through decoration-muted-foreground/50">
                  5 lessons from removing standups
                </span>
                <span className="key key-lite shrink-0 rounded-md px-1.5 py-0.5 text-[11px] tabular-nums text-muted-foreground">1.9/5</span>
              </div>
            </div>
            <h3 className="mt-5 font-semibold">You never know what will land</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
              Every draft is scored before you post, so the flops get fixed before anyone sees them.
            </p>
          </div>
        </div>
      </section>

      <ProductFlowLoop />

      {/* Testimonials Section */}
      <Testimonials />

      {/* The Chrome extension, without screenshots: the shortcut is the product */}
      <section className="px-6 py-16">
        <div className="slab mx-auto flex max-w-5xl flex-col gap-6 rounded-2xl border border-border bg-card p-6 md:flex-row md:items-center md:p-8">
          <div className="flex shrink-0 items-center gap-2" aria-hidden="true">
            <kbd className="key key-lite key-caps flex h-14 w-14 items-center justify-center rounded-xl border border-border text-2xl">`</kbd>
            <span className="text-sm text-muted-foreground">or</span>
            <kbd className="key key-lite key-caps flex h-14 items-center justify-center rounded-xl border border-border px-3 text-sm">{isMac ? 'Option' : 'Alt'}</kbd>
            <kbd className="key key-lite key-caps flex h-14 w-14 items-center justify-center rounded-xl border border-border text-lg">T</kbd>
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-semibold tracking-tight">The same chat, on any page</h2>
            <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
              Press the key and the chat drops down over the page you are reading, with that page as context: a tweet, a
              YouTube video, a GitHub repo, an article. Reply, quote or turn it into a post without switching tabs.
            </p>
          </div>
          <Button
            variant="outline"
            className="shrink-0"
            onClick={() => {
              pulse.trackEvent('add_to_chrome_clicked', { location: 'extension_strip', authenticated: isAuthenticated })
              window.open('https://chromewebstore.google.com/detail/fnpbcidkcfbbijjnibgfhdablepmmefk', '_blank')
            }}
          >
            <Chrome className="mr-2 h-4 w-4" />
            Add to Chrome
          </Button>
        </div>
      </section>

      {/* Pricing or Quick Actions */}
      <div id="pricing" className="scroll-mt-24 px-6 py-16 bg-background">
        <div className="max-w-6xl mx-auto">
          {isAuthenticated ? (
            /* Quick Actions for logged-in users */
            <>
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold mb-2">Content creation hub</h2>
                <p className="text-muted-foreground">Ready to create content? Start with a voice call or explore your options.</p>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-8 max-w-3xl mx-auto">
                {/* Account Status */}
                <AccountStatus />

                {/* Quick Actions */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Activity className="w-5 h-5" />
                      Quick Actions
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <Button 
                        onClick={() => navigate('/scheduler')} 
                        className="w-full bg-foreground hover:bg-foreground/90 text-background"
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        Schedule Voice Calls
                      </Button>
                      
                      <Button 
                        onClick={() => navigate('/chat')} 
                        variant="outline"
                        className="w-full"
                      >
                        <BarChart3 className="w-4 h-4 mr-2" />
                        View Dashboard
                      </Button>
                      
                      <div className="text-center text-sm text-muted-foreground">
                        <p>Need content right now?</p>
                        <p>Use the "Call Me Now" button above to speak with Claude instantly!</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          ) : (
            /* Pricing for non-authenticated users */
            <>
              <div className="text-center mb-10">
                <h2 className="text-3xl font-bold mb-2">Pricing</h2>
                <p className="text-muted-foreground">One plan, billed weekly. 7-day free trial.</p>
              </div>

              <div className="slab mx-auto max-w-sm rounded-2xl border border-border bg-card p-6">
                <div className="flex items-baseline justify-between">
                  <h3 className="text-base font-semibold">X11</h3>
                  <div className="text-3xl font-semibold tabular-nums">
                    $4.79
                    <span className="text-sm font-normal text-muted-foreground">/week</span>
                  </div>
                </div>
                <div className="mt-4 space-y-1.5 text-sm">
                  <p>The full product: creator chat, voice calls, drafting and scoring, the queue, analytics, direct X publishing.</p>
                  <p className="text-muted-foreground">7 voice minutes topped up every week. Cancel anytime.</p>
                </div>
                <Button
                  onClick={() => handleSelectPlan('starter')}
                  className="mt-6 w-full bg-foreground hover:bg-foreground/90 text-background"
                >
                  Start free trial
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* FAQ Section */}
      <div id="faq" className="scroll-mt-24 px-6 py-16 bg-background">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center">Frequently asked questions</h2>
          
          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((item, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>

      {/* Voice Widget - Floating draggable interface */}
      {/* Demo Widget for non-authenticated users */}
      {!isAuthenticated && (
        <VoiceWidget
          isActive={demoCallActive && conversation.status === 'connected'}
          isSpeaking={conversation.isSpeaking}
          onStop={handleEndDemoCall}
          onCancel={handleEndDemoCall}
          isDemo={true}
          demoMinutesRemaining={demoMinutesData?.getDemoMinutesStatus?.minutesRemaining}
          getInputByteFrequencyData={conversation.getInputByteFrequencyData}
          getOutputByteFrequencyData={conversation.getOutputByteFrequencyData}
        />
      )}
      
      {/* User Widget for authenticated users */}
      {isAuthenticated && (
        <VoiceWidget
          isActive={userCallActive && conversation.status === 'connected'}
          isSpeaking={conversation.isSpeaking}
          onStop={handleEndUserCall}
          onCancel={handleEndUserCall}
          isDemo={false}
          demoMinutesRemaining={user?.credits}
          getInputByteFrequencyData={conversation.getInputByteFrequencyData}
          getOutputByteFrequencyData={conversation.getOutputByteFrequencyData}
        />
      )}

      {/* Footer */}
      <Footer />
    </div>
  )
}
